"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Label } from "../../ui/label";
import { ArrowLeft, CheckCircle, AlertCircle, Save, Loader2, Edit2, Lightbulb } from "lucide-react";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "../../../lib/textron-data-processor";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../ui/tooltip";
import { SHAREPOINT_FY_YEAR } from "@/constants/sharepoint-config";

interface LitigationsClaimsAssessmentProps {
  onBack?: () => void;
  area?: "Planning" | "Execution" | "ConcludingProcedures";
}

interface QuestionResponse {
  selectedOption?: string;
  textResponse?: { html: string; plainText: string };
  documents?: string[];
  lastUpdated?: string;
  companyName?: string;
}

interface QuestionState {
  selectedOption: string;
  textResponse: { html: string; plainText: string } | null;
  documents: any[];
}

// Question IDs
const QUESTION_IDS = {
  Q1: "LIT-ASSESS-01",
  Q2: "LIT-ASSESS-02",
  Q2_DOC: "LIT-ASSESS-02-doc",
  Q3: "LIT-ASSESS-03",
  Q3_DOC: "LIT-ASSESS-03-doc",
  Q4: "LIT-ASSESS-04",
  Q4_ALTERNATIVE: "LIT-ASSESS-04-alternative",
  Q4_MODIFY: "LIT-ASSESS-04-modify",
} as const;

export default function LitigationsClaimsAssessment({ onBack, area = "Execution" }: LitigationsClaimsAssessmentProps) {
  // Even if accessed from Concluding Procedures, use Execution data and section keys
  // because the substantive procedure was created in Execution phase
  const effectiveArea = "Execution";
  const [companyName, setCompanyName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [sharePointSaveStatus, setSharePointSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [questionResponses, setQuestionResponses] = useState<Record<string, QuestionState>>({});
  const [dirtyQuestions, setDirtyQuestions] = useState<Set<string>>(new Set());

  // Initialize question states
  useEffect(() => {
    const initialStates: Record<string, QuestionState> = {};
    Object.values(QUESTION_IDS).forEach((id) => {
      initialStates[id] = {
        selectedOption: "",
        textResponse: null,
        documents: [],
      };
    });
    setQuestionResponses(initialStates);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

        await loadResponses(name);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const loadResponses = async (companyName: string) => {
    try {
      if ((window as any).sharePointAPI?.downloadClientJson) {
        const res = await (window as any).sharePointAPI.downloadClientJson("Execution_LIT_Assessment.json");
        if (res.success && res.data) {
          const assessmentData = res.data.assessment || {};
          
          const initialAnswers: Record<string, string> = {};
          const initialResponses: Record<string, QuestionState> = {};

          Object.values(QUESTION_IDS).forEach((qId) => {
            const response = assessmentData[qId] as QuestionResponse | undefined;
            if (response) {
              initialAnswers[qId] = response.selectedOption || "";
              initialResponses[qId] = {
                selectedOption: response.selectedOption || "",
                textResponse: response.textResponse || null,
                documents: (response.documents || []).map((doc: string) => ({
                  id: `doc_${doc}`,
                  type: 'linked' as const,
                  name: doc
                })),
              };
            } else {
              initialResponses[qId] = {
                selectedOption: "",
                textResponse: null,
                documents: [],
              };
            }
          });

          setAnswers(initialAnswers);
          setQuestionResponses(initialResponses);
        }
      }
    } catch (error) {
      console.error("Error loading responses:", error);
    }
  };

  const handleAnswerChange = (questionId: string, answer: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId] || { selectedOption: "", textResponse: null, documents: [] },
        selectedOption: answer
      }
    }));
    setDirtyQuestions(prev => new Set(prev).add(questionId));

    // Initialize Q4-modify when Q4-alternative is "No"
    if (questionId === QUESTION_IDS.Q4_ALTERNATIVE && answer === "No") {
      setQuestionResponses(prev => ({
        ...prev,
        [QUESTION_IDS.Q4_MODIFY]: prev[QUESTION_IDS.Q4_MODIFY] || {
          selectedOption: "",
          textResponse: null,
          documents: [],
        }
      }));
    }
  };

  const handleDocumentsChange = (questionId: string, documents: any[]) => {
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId] || { selectedOption: "", textResponse: null, documents: [] },
        documents: documents.map(doc => doc.name || doc)
      }
    }));
    setDirtyQuestions(prev => new Set(prev).add(questionId));
  };

  const handleTextResponseChange = (questionId: string, textResponse: any) => {
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId] || { selectedOption: "", textResponse: null, documents: [] },
        textResponse
      }
    }));
    setDirtyQuestions(prev => new Set(prev).add(questionId));
  };

  const isQuestionAnswered = (questionId: string) => {
    const hasOptionAnswer =
      answers[questionId] !== undefined && answers[questionId] !== "";

    const response = questionResponses[questionId];
    const hasTextResponse =
      !!response?.textResponse &&
      (
        (response.textResponse.html && response.textResponse.html.trim() !== "") ||
        (response.textResponse.plainText && response.textResponse.plainText.trim() !== "")
      );
    const hasDocuments =
      !!response?.documents && Array.isArray(response.documents) && response.documents.length > 0;
    const hasTable =
      !!response?.table && Array.isArray(response.table) && response.table.length > 0;

    return hasOptionAnswer || hasTextResponse || hasDocuments || hasTable;
  };

  const sanitizeForJSON = (obj: any): any => {
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    if (typeof obj !== 'object') {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => sanitizeForJSON(item));
    }
    
    if (obj instanceof Date) {
      return obj.toISOString();
    }
    
    const sanitized: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key];
        if (typeof value === 'function' || typeof value === 'symbol') {
          continue;
        }
        sanitized[key] = sanitizeForJSON(value);
      }
    }
    return sanitized;
  };

  const saveResponses = useCallback(async () => {
    if (!companyName) return;

    try {
      // Trigger auto-save for all DocumentManager instances
      Object.values(QUESTION_IDS).forEach((qId) => {
        const autoSaveMethod = (window as any)[`autoSaveTextbox_Execution_LIT_${qId}`];
        if (autoSaveMethod && typeof autoSaveMethod === 'function') {
          autoSaveMethod();
        }
      });
      await new Promise(resolve => setTimeout(resolve, 1000));

      setSharePointSaveStatus('saving');
      setSaveError(null);

      // Load existing file
      let existingData: any = {};
      if ((window as any).sharePointAPI?.downloadClientJson) {
        const res = await (window as any).sharePointAPI.downloadClientJson("Execution_LIT_Assessment.json");
        if (res.success && res.data) {
          existingData = res.data;
        }
      }

      if (!existingData.assessment) {
        existingData.assessment = {};
      }

      const assessmentData: Record<string, QuestionResponse> = {};

      Object.values(QUESTION_IDS).forEach((qId) => {
        const state = questionResponses[qId] || { selectedOption: "", textResponse: null, documents: [] };
        const textboxFromLocalStorage = localStorage.getItem(`textbox_Execution_LIT_${qId}`);
        const hasTextboxContent = textboxFromLocalStorage && textboxFromLocalStorage.trim().length > 0;

        const fileDocuments = (state.documents || []).filter((d: any) => d.type !== 'textbox');
        const documentNames = fileDocuments.map((d: any) => {
          if (typeof d === 'string') return d;
          if (d && typeof d === 'object' && d.name) return d.name;
          return String(d);
        });

        let textResponse = { html: "", plainText: "" };
        if (hasTextboxContent) {
          textResponse = {
            html: textboxFromLocalStorage,
            plainText: textboxFromLocalStorage.replace(/<[^>]*>/g, '')
          };
        } else if (state.textResponse) {
          textResponse = state.textResponse;
        }

        assessmentData[qId] = {
          selectedOption: state.selectedOption || "",
          textResponse: textResponse,
          documents: documentNames,
          lastUpdated: new Date().toISOString(),
          companyName: companyName,
        };
      });

      // Merge into existing data
      Object.entries(assessmentData).forEach(([qId, value]) => {
        existingData.assessment[qId] = value;
      });

      // Sanitize the data before stringifying
      const sanitizedData = sanitizeForJSON(existingData);
      const jsonString = JSON.stringify(sanitizedData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: "Execution_LIT_Assessment.json",
        referenceValue: "Execution Litigations & Claims Assessment Responses",
        folderName: "juggernaut",
        fyYear: String(SHAREPOINT_FY_YEAR),
        sectionList: ["Execution_LIT_Assessment"],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save responses");
      }

      setSharePointSaveStatus('saved');
      setDirtyQuestions(new Set());
      setIsEditMode(false);
    } catch (error: any) {
      console.error("Error saving responses:", error);
      setSharePointSaveStatus('error');
      setSaveError(error?.message || "Failed to save responses");
    }
  }, [companyName, questionResponses, dirtyQuestions]);

  const handleComplete = async () => {
    if (isEditMode) {
      try {
        setIsSaving(true);
        await saveResponses();
      } catch (error) {
        console.error("Error completing:", error);
      } finally {
        setIsSaving(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div>
            <h2 className="text-xl font-semibold text-white">Assessment</h2>
            <p className="text-sm text-white/60">Loading questions...</p>
          </div>
        </div>
        <Card className="border-white/10 bg-white/5 text-white">
          <CardContent className="p-6">
            <div className="text-center text-white/60">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4da3ff] mx-auto mb-4"></div>
              <p>Loading questions...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const showQ2Document = answers[QUESTION_IDS.Q2] === "Yes";
  const showQ3Document = answers[QUESTION_IDS.Q3] === "Yes";
  const showQ4Alternative = answers[QUESTION_IDS.Q4] === "Yes";
  const showQ4Modify = showQ4Alternative && answers[QUESTION_IDS.Q4_ALTERNATIVE] === "No";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack} className="text-white/80 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}
          <div>
            <h2 className="text-xl font-semibold text-white">Assessment</h2>
            <p className="text-sm text-white/60">Answer the following questions for assessment procedures.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditMode ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditMode(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleComplete}
                disabled={isSaving}
                className="bg-[#4da3ff] hover:bg-[#4da3ff]/80"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={() => setIsEditMode(true)}>
              <Edit2 className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </div>

      {saveError && (
        <Card className="border-red-500/20 bg-red-500/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-300">
              <AlertCircle className="h-5 w-5" />
              <p>{saveError}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {sharePointSaveStatus === 'saved' && (
        <Card className="border-green-500/20 bg-green-500/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-green-300">
              <CheckCircle className="h-5 w-5" />
              <p>Responses saved successfully</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {/* Question 1 */}
        <Card className="border-white/10 bg-white/5 text-white">
          <CardContent className="p-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  {isQuestionAnswered(QUESTION_IDS.Q1) ? (
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-white/40" />
                  )}
                </div>
                <div className="flex-1">
                  <Label className="text-sm font-medium text-white">
                    1. Document our procedures and confirmations sent to entity's external legal counsel through letter of general inquiry. [Document]
                  </Label>
                </div>
              </div>
              <div className="ml-8 mt-3">
                <DocumentManager
                  sectionKey="execution-substantive:Litigations & Claims"
                  itemId={QUESTION_IDS.Q1}
                  itemQuestion="Document our procedures and confirmations sent to entity's external legal counsel through letter of general inquiry."
                  itemTextResponse={questionResponses[QUESTION_IDS.Q1]?.textResponse}
                  itemDocuments={questionResponses[QUESTION_IDS.Q1]?.documents?.map((doc: string | any) => {
                    if (typeof doc === 'string') {
                      return {
                        id: `doc_${doc}`,
                        type: 'linked' as const,
                        name: doc
                      };
                    }
                    return doc;
                  }) || []}
                  onDocumentsChange={(docs) => handleDocumentsChange(QUESTION_IDS.Q1, docs)}
                  onTextResponseChange={(textResp) => handleTextResponseChange(QUESTION_IDS.Q1, textResp)}
                  disabled={!isEditMode}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Question 2 */}
        <Card className="border-white/10 bg-white/5 text-white">
          <CardContent className="p-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  {isQuestionAnswered(QUESTION_IDS.Q2) ? (
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-white/40" />
                  )}
                </div>
                <div className="flex-1">
                  <Label className="text-sm font-medium text-white">
                    2. Is it considered unlikely that the entity's external legal counsel will respond appropriately to a letter of general inquiry?
                  </Label>
                </div>
              </div>
              <RadioGroup
                value={answers[QUESTION_IDS.Q2] || ""}
                onValueChange={(value) => handleAnswerChange(QUESTION_IDS.Q2, value)}
                disabled={!isEditMode}
                className="ml-8"
              >
                {["Yes", "No"].map((option) => (
                  <div key={option} className="flex items-center space-x-2">
                    <RadioGroupItem 
                      value={option} 
                      id={`${QUESTION_IDS.Q2}-${option}`}
                      className="border-white/20 text-[#4da3ff] focus:ring-[#4da3ff]/20"
                    />
                    <Label 
                      htmlFor={`${QUESTION_IDS.Q2}-${option}`}
                      className="text-sm text-white/80 cursor-pointer"
                    >
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              {showQ2Document && (
                <div className="ml-8 mt-3 border-l-2 border-l-[#4da3ff]/30 pl-4">
                  <Label className="text-sm font-medium text-white mb-2 block">
                    If Yes, Document communications through letter of specific inquiry including:
                  </Label>
                  <ul className="text-sm text-white/80 mb-3 space-y-1 list-disc list-inside">
                    <li>A list of litigation and claims;</li>
                    <li>Where available, management's assessment of the outcome of each of the identified litigation and claims and its estimate of the financial implications, including costs involved; and</li>
                    <li>A request that the entity's external legal counsel confirm the reasonableness of management's assessments and provide the auditor with further information if the list is considered by the entity's external legal counsel to be incomplete or incorrect.</li>
                  </ul>
                  <DocumentManager
                    sectionKey="execution-substantive:Litigations & Claims"
                    itemId={QUESTION_IDS.Q2_DOC}
                    itemQuestion="Document communications through letter of specific inquiry"
                    itemTextResponse={questionResponses[QUESTION_IDS.Q2_DOC]?.textResponse}
                    itemDocuments={questionResponses[QUESTION_IDS.Q2_DOC]?.documents?.map((doc: string | any) => {
                      if (typeof doc === 'string') {
                        return {
                          id: `doc_${doc}`,
                          type: 'linked' as const,
                          name: doc
                        };
                      }
                      return doc;
                    }) || []}
                    onDocumentsChange={(docs) => handleDocumentsChange(QUESTION_IDS.Q2_DOC, docs)}
                    onTextResponseChange={(textResp) => handleTextResponseChange(QUESTION_IDS.Q2_DOC, textResp)}
                    disabled={!isEditMode}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Question 3 */}
        <Card className="border-white/10 bg-white/5 text-white">
          <CardContent className="p-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  {isQuestionAnswered(QUESTION_IDS.Q3) ? (
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-white/40" />
                  )}
                </div>
                <div className="flex-1">
                  <Label className="text-sm font-medium text-white flex items-center gap-2">
                    <span>3. Have we judged it necessary to meet with the entity's external legal counsel to discuss the likely outcome of the litigation or claims?</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 text-white/60 hover:text-white hover:bg-white/10"
                          >
                            <Lightbulb className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          className="max-w-md p-3 bg-gray-900 text-white border border-gray-700 whitespace-pre-line"
                        >
                          <p className="text-sm">This may be the case, for example, where:</p>
                          <ul className="text-sm mt-2 space-y-1 list-disc list-inside">
                            <li>The auditor determines that the matter is a significant risk;</li>
                            <li>The matter is complex.</li>
                            <li>There is disagreement between management and the entity's external legal counsel.</li>
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Label>
                </div>
              </div>
              <RadioGroup
                value={answers[QUESTION_IDS.Q3] || ""}
                onValueChange={(value) => handleAnswerChange(QUESTION_IDS.Q3, value)}
                disabled={!isEditMode}
                className="ml-8"
              >
                {["Yes", "No"].map((option) => (
                  <div key={option} className="flex items-center space-x-2">
                    <RadioGroupItem 
                      value={option} 
                      id={`${QUESTION_IDS.Q3}-${option}`}
                      className="border-white/20 text-[#4da3ff] focus:ring-[#4da3ff]/20"
                    />
                    <Label 
                      htmlFor={`${QUESTION_IDS.Q3}-${option}`}
                      className="text-sm text-white/80 cursor-pointer"
                    >
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              {showQ3Document && (
                <div className="ml-8 mt-3 border-l-2 border-l-[#4da3ff]/30 pl-4">
                  <Label className="text-sm font-medium text-white mb-2 block">
                    If Yes, Document
                  </Label>
                  <DocumentManager
                    sectionKey="execution-substantive:Litigations & Claims"
                    itemId={QUESTION_IDS.Q3_DOC}
                    itemQuestion="Document meeting with the entity's external legal counsel"
                    itemTextResponse={questionResponses[QUESTION_IDS.Q3_DOC]?.textResponse}
                    itemDocuments={questionResponses[QUESTION_IDS.Q3_DOC]?.documents?.map((doc: string | any) => {
                      if (typeof doc === 'string') {
                        return {
                          id: `doc_${doc}`,
                          type: 'linked' as const,
                          name: doc
                        };
                      }
                      return doc;
                    }) || []}
                    onDocumentsChange={(docs) => handleDocumentsChange(QUESTION_IDS.Q3_DOC, docs)}
                    onTextResponseChange={(textResp) => handleTextResponseChange(QUESTION_IDS.Q3_DOC, textResp)}
                    disabled={!isEditMode}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Question 4 */}
        <Card className="border-white/10 bg-white/5 text-white">
          <CardContent className="p-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  {isQuestionAnswered(QUESTION_IDS.Q4) ? (
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-white/40" />
                  )}
                </div>
                <div className="flex-1">
                  <Label className="text-sm font-medium text-white">
                    4. Has the management refused to give us permission to communicate or meet with the entity's external legal counsel, or the entity's external legal counsel has refused to respond appropriately to the letter of inquiry, or is prohibited from responding?
                  </Label>
                </div>
              </div>
              <RadioGroup
                value={answers[QUESTION_IDS.Q4] || ""}
                onValueChange={(value) => handleAnswerChange(QUESTION_IDS.Q4, value)}
                disabled={!isEditMode}
                className="ml-8"
              >
                {["Yes", "No"].map((option) => (
                  <div key={option} className="flex items-center space-x-2">
                    <RadioGroupItem 
                      value={option} 
                      id={`${QUESTION_IDS.Q4}-${option}`}
                      className="border-white/20 text-[#4da3ff] focus:ring-[#4da3ff]/20"
                    />
                    <Label 
                      htmlFor={`${QUESTION_IDS.Q4}-${option}`}
                      className="text-sm text-white/80 cursor-pointer"
                    >
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              {showQ4Alternative && (
                <div className="ml-8 mt-3 border-l-2 border-l-[#4da3ff]/30 pl-4">
                  <Label className="text-sm font-medium text-white mb-2 block">
                    If Yes, Are we able to obtain sufficient appropriate audit evidence by performing alternative audit procedures?
                  </Label>
                  <RadioGroup
                    value={answers[QUESTION_IDS.Q4_ALTERNATIVE] || ""}
                    onValueChange={(value) => handleAnswerChange(QUESTION_IDS.Q4_ALTERNATIVE, value)}
                    disabled={!isEditMode}
                    className="ml-4"
                  >
                    {["Yes", "No"].map((option) => (
                      <div key={option} className="flex items-center space-x-2">
                        <RadioGroupItem 
                          value={option} 
                          id={`${QUESTION_IDS.Q4_ALTERNATIVE}-${option}`}
                          className="border-white/20 text-[#4da3ff] focus:ring-[#4da3ff]/20"
                        />
                        <Label 
                          htmlFor={`${QUESTION_IDS.Q4_ALTERNATIVE}-${option}`}
                          className="text-sm text-white/80 cursor-pointer"
                        >
                          {option}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                  {showQ4Modify && (
                    <div className="ml-4 mt-3 border-l-2 border-l-red-500/30 pl-4">
                      <Label className="text-sm font-medium text-white mb-2 block">
                        If No, Modify the opinion in the auditor's report in accordance with SA 705.
                      </Label>
                      <RadioGroup
                        value={questionResponses[QUESTION_IDS.Q4_MODIFY]?.selectedOption || ""}
                        onValueChange={(value) => handleAnswerChange(QUESTION_IDS.Q4_MODIFY, value)}
                        disabled={!isEditMode}
                        className="ml-4"
                      >
                        {["Yes", "No"].map((option) => (
                          <div key={option} className="flex items-center space-x-2">
                            <RadioGroupItem 
                              value={option} 
                              id={`${QUESTION_IDS.Q4_MODIFY}-${option}`}
                              className="border-white/20 text-[#4da3ff] focus:ring-[#4da3ff]/20"
                            />
                            <Label 
                              htmlFor={`${QUESTION_IDS.Q4_MODIFY}-${option}`}
                              className="text-sm text-white/80 cursor-pointer"
                            >
                              {option}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

